<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";
import { isHeicMedia } from "~/utils/is-heic-media";

const props = defineProps({
  fileObject: {
    type: Object,
    required: true,
  },
});

const rotation = ref(0);
const isInside = ref(false);
const naturalWidth = ref(0);
const naturalHeight = ref(0);
const previewUrl = ref("");
const isLoading = ref(true);
const hasError = ref(false);
const retryCount = ref(0);

const isRotated = computed(() => Math.abs(rotation.value % 180) === 90);

const containerStyle = computed(() => {
  if (naturalWidth.value && naturalHeight.value) {
    const aspectRatio = isRotated.value
      ? naturalHeight.value / naturalWidth.value
      : naturalWidth.value / naturalHeight.value;

    return {
      width: "100%",
      aspectRatio: `${aspectRatio}`,
    };
  }
  return { width: "100%" };
});

const onImgLoad = (e: Event) => {
  const img = e.target as HTMLImageElement;
  naturalWidth.value = img.naturalWidth;
  naturalHeight.value = img.naturalHeight;
  isLoading.value = false;
};

const onImgError = () => {
  isLoading.value = false;
  hasError.value = true;
};

const rotate = (direction: "clockwise" | "counterclockwise") => {
  if (direction === "clockwise") {
    rotation.value += 90;
  } else {
    rotation.value -= 90;
  }
};

const scale = ref(1);

const zoom = (direction: "in" | "out") => {
  const step = 0.1;
  if (direction === "in") {
    scale.value = Math.min(scale.value + step, 3); // max 3x zoom
  } else {
    scale.value = Math.max(scale.value - step, 0.5); // min 0.5x zoom
  }
};

const offsetX = ref(0);
const offsetY = ref(0);

const isDragging = ref(false);
const lastMouse = ref({ x: 0, y: 0 });

const startDrag = (e: MouseEvent | TouchEvent) => {
  isDragging.value = true;
  if (e instanceof MouseEvent) {
    lastMouse.value = { x: e.clientX, y: e.clientY };
  } else {
    lastMouse.value = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  }
};

const drag = (e: MouseEvent | TouchEvent) => {
  if (!isDragging.value || scale.value <= 1) return;
  let currentX, currentY;

  if (e instanceof MouseEvent) {
    currentX = e.clientX;
    currentY = e.clientY;
  } else {
    currentX = e.touches[0].clientX;
    currentY = e.touches[0].clientY;
  }

  const dx = currentX - lastMouse.value.x;
  const dy = currentY - lastMouse.value.y;

  offsetX.value += dx;
  offsetY.value += dy;

  lastMouse.value = { x: currentX, y: currentY };
};

const endDrag = () => {
  isDragging.value = false;
};

watch(scale, (newVal) => {
  if (newVal <= 1) {
    offsetX.value = 0;
    offsetY.value = 0;
  }
});

let stopPreview: (() => void) | undefined;
onMounted(() => {
  stopPreview = watch(
    () => [props.fileObject.mediaHref, isHeicMedia(props.fileObject), retryCount.value] as const,
    async ([source, needsConversion], _previous, onCleanup) => {
      const controller = new AbortController();
      let objectUrl: string | undefined;
      onCleanup(() => {
        controller.abort();
        if (objectUrl) URL.revokeObjectURL(objectUrl);
      });

      previewUrl.value = "";
      hasError.value = false;
      isLoading.value = true;
      naturalWidth.value = naturalHeight.value = 0;
      rotation.value = offsetX.value = offsetY.value = 0;
      scale.value = 1;
      isDragging.value = false;

      if (!source) {
        onImgError();
        return;
      }
      if (!needsConversion) {
        previewUrl.value = source;
        return;
      }

      try {
        // Load the decoder only for HEIC previews, and only in the browser.
        const [{ heicTo }, response] = await Promise.all([
          import("heic-to/csp"),
          fetch(source, { signal: controller.signal }),
        ]);
        if (!response.ok) throw new Error("Image download failed");
        const blob = await response.blob();
        if (controller.signal.aborted) return;
        const jpeg = await heicTo({ blob, type: "image/jpeg", quality: 0.9 });
        // Decoding cannot be aborted, so ignore results from an old attachment.
        if (controller.signal.aborted) return;
        objectUrl = URL.createObjectURL(jpeg);
        previewUrl.value = objectUrl;
      } catch {
        if (!controller.signal.aborted) onImgError();
      }
    },
    { immediate: true }
  );
});
onBeforeUnmount(() => stopPreview?.());
</script>

<template>
  <div
    class="relative bg-black overflow-hidden"
    :style="containerStyle"
    v-auto-animate
  >
    <div v-if="isLoading" role="status" class="flex min-h-40 items-center justify-center p-6 text-white">
      Loading image…
    </div>
    <div v-if="hasError" role="alert" class="flex min-h-40 flex-col items-center justify-center gap-3 p-6 text-center text-white">
      <p>This image couldn't be previewed.</p>
      <button type="button" class="rounded border border-white px-4 py-2 hover:bg-white/10" @click="retryCount++">
        Retry
      </button>
      <a v-if="fileObject.mediaHref" :href="fileObject.mediaHref" target="_blank" rel="noopener noreferrer" class="underline">
        Open original file
      </a>
    </div>
    <div
      v-if="!hasError"
      class="w-full h-full flex items-center justify-center"
      @mouseenter="isInside = true"
      @mouseleave="isInside = false"
    >
      <img
        v-if="previewUrl"
        v-show="!isLoading"
        :key="previewUrl"
        :src="previewUrl"
        :alt="fileObject.originalFileName || fileObject.name || 'Submitted image'"
        class="max-w-full max-h-full transition-transform duration-300 cursor-grab active:cursor-grabbing select-none"
        :style="{
          transform: `
      translate(${offsetX}px, ${offsetY}px)
      rotate(${rotation}deg)
      scale(${scale})
    `,
          transformOrigin: 'center center',
        }"
        @load="onImgLoad"
        @error="onImgError"
        @mousedown="startDrag"
        @touchstart="startDrag"
        @mousemove="drag"
        @touchmove="drag"
        @mouseup="endDrag"
        @mouseleave="endDrag"
        @touchend="endDrag"
        @dragstart.prevent
      />
    </div>

    <div
      v-if="!isLoading && !hasError"
      class="absolute top-0 w-full bg-opacity-10 p-2 flex flex-row justify-end bg-dark/30 z-[48] gap-2"
    >
      <BaseIconButton :size="40" aria-label="Zoom out" @click="zoom('out')">
        <icon name="lucide:zoom-out" class="text-primary-on" />
      </BaseIconButton>
      <BaseIconButton :size="40" aria-label="Zoom in" @click="zoom('in')">
        <icon name="lucide:zoom-in" class="text-primary-on" />
      </BaseIconButton>
      <BaseIconButton :size="40" aria-label="Rotate counterclockwise" @click="rotate('counterclockwise')">
        <icon name="lucide:rotate-ccw" class="text-primary-on" />
      </BaseIconButton>
      <BaseIconButton :size="40" aria-label="Rotate clockwise" @click="rotate('clockwise')">
        <icon name="lucide:rotate-cw" class="text-primary-on" />
      </BaseIconButton>
    </div>
  </div>
</template>

<style scoped></style>
